import type { IconName } from '@/components/synapse/Icon';
import type { PermissionKey } from '@/lib/permissions';

// `labelKey` values are i18n translation keys (resolved with `t(leaf.labelKey)`
// at render time). Keep them in sync with the `nav.*` group in locale files.
export interface NavLeaf {
  to: string;
  labelKey: string;
  icon: IconName;
  /**
   * Permission needed to see this entry. Absent means "any member".
   *
   * This replaced an `adminOnly` boolean, which could only express one
   * distinction and quietly meant three different things depending on the leaf
   * (doctor, manager, auditor). Naming the key makes each one say which.
   */
  requires?: PermissionKey;
}

export type NavGroupId = 'appointments' | 'lab' | 'stocks' | 'admin';

export interface NavGroup {
  id: NavGroupId;
  labelKey: string;
  icon: IconName;
  items: NavLeaf[];
}

export const HOME: NavLeaf = { to: '/', labelKey: 'nav.home', icon: 'home' };
// Patients is the doctor's full clinical record (history + consultations), so
// it needs the clinical read key. A top-level leaf rendered like HOME (not
// inside a group), placed right after it. Members without it use Registry.
export const PATIENTS: NavLeaf = {
  to: '/patients',
  labelKey: 'nav.patients',
  icon: 'user',
  requires: 'clinical.consultations.read',
};

// The doctor's working screen during a session — the patient holding the number
// being served, and their past visits. Top-level beside PATIENTS for the same
// reason: it is the clinical surface, and it needs the same key.
export const CONSULTING: NavLeaf = {
  to: '/consulting',
  labelKey: 'nav.consulting',
  icon: 'eye',
  requires: 'clinical.consultations.read',
};

// Patient registry — search + register. The history-free counterpart to
// PATIENTS, under the Appointments group.
//
// Every leaf below names the key its destination's API actually enforces. They
// were briefly left bare on the theory that these are "things every member
// does" — which was true under the old admin/staff enum, where the narrowest
// role still held all of them. With custom roles it stopped being true, and a
// bare leaf became a door that opens onto a 403.
const REGISTRY: NavLeaf = {
  to: '/registry',
  labelKey: 'nav.registry',
  icon: 'users',
  requires: 'patients.read',
};
const APT_COUNTER: NavLeaf = {
  to: '/apt-counter',
  labelKey: 'nav.aptCounter',
  icon: 'calendar',
  requires: 'apt_counter.read',
};
// Where staff type up the visit the doctor wrote on paper. `edit` because it is
// the one leaf whose whole job is writing something down.
const VISITS: NavLeaf = {
  to: '/visits',
  labelKey: 'nav.recordVisit',
  icon: 'edit',
  requires: 'visits.record',
};
// The lab desk. `flask` was already in the icon set and used by no other leaf;
// `list` and `history` are taken, and two leaves wearing the same glyph read as
// the same destination.
//
// Worklist and Reports both name `lab.read` — the split that matters is inside
// Reports, where opening a PDF additionally needs `lab.results.read`. A member
// without it still has a use for the page (finding out whether a result is
// back), so gating the leaf on the stricter key would hide something they are
// allowed to see.
const LAB_WORKLIST: NavLeaf = {
  to: '/lab',
  labelKey: 'nav.labWorklist',
  icon: 'flask',
  requires: 'lab.read',
};
const LAB_REPORTS: NavLeaf = {
  to: '/lab/reports',
  labelKey: 'nav.labReports',
  icon: 'file',
  requires: 'lab.read',
};
const LAB_CONFIG: NavLeaf = {
  to: '/lab/catalogue',
  labelKey: 'nav.labConfig',
  icon: 'settings',
  requires: 'lab.catalogue.manage',
};
const STOCK: NavLeaf = {
  to: '/stock',
  labelKey: 'nav.stock',
  icon: 'box',
  requires: 'inventory.items.read',
};
const ADD_STOCK: NavLeaf = {
  to: '/add',
  labelKey: 'nav.addStock',
  icon: 'box_in',
  requires: 'inventory.stock.in',
};
const ISSUE: NavLeaf = {
  to: '/issue',
  labelKey: 'nav.issue',
  icon: 'box_out',
  requires: 'inventory.stock.issue',
};
const ADJUST: NavLeaf = {
  to: '/adjust',
  labelKey: 'nav.adjust',
  icon: 'adjust',
  requires: 'inventory.stock.adjust',
};
// The stock ledger, one line per movement, and the only place a mistyped entry
// can be corrected. Gated on reading movements, not on correcting them: the
// person who typed 100 instead of 10 needs to see the line before they can ask
// anyone to fix it, and the API's latest-movement-per-batch rule is what keeps
// the correction itself safe.
//
// `list` rather than `history` — the audit log already owns that icon, and two
// leaves wearing the same glyph read as the same destination.
const ACTIVITY: NavLeaf = {
  to: '/activity',
  labelKey: 'nav.activity',
  icon: 'list',
  requires: 'inventory.movements.read',
};
// Gated on `analytics.read` in all three places — `requires` here,
// `RequirePermission` on the route, `requirePermission` on the API.
//
// It sits in two different places on the two surfaces, on purpose. On desktop it
// joins the Stocks group, because it answers a question about stock and that is
// where someone would look for it. On mobile the `stocks` view is the dispenser's
// working set — add / issue / adjust — and a gated entry would be filtered out
// of it for most dispensers anyway, so it goes with the other oversight
// surfaces instead of leaving a gap in a four-item tab bar.
const ISSUING_ANALYTICS: NavLeaf = {
  to: '/analytics/issuing',
  labelKey: 'nav.issuingAnalytics',
  icon: 'chart',
  requires: 'analytics.read',
};
const PEOPLE: NavLeaf = {
  to: '/people',
  labelKey: 'nav.people',
  icon: 'users',
  requires: 'members.read',
};
const AUDIT_LOG: NavLeaf = {
  to: '/audit-log',
  labelKey: 'nav.auditLog',
  icon: 'history',
  requires: 'audit.read',
};
const ROLES: NavLeaf = {
  to: '/roles',
  labelKey: 'nav.roles',
  icon: 'settings',
  requires: 'roles.read',
};

export const GROUPS: NavGroup[] = [
  { id: 'appointments', labelKey: 'nav.appointments', icon: 'calendar', items: [REGISTRY, APT_COUNTER, VISITS] },
  {
    id: 'lab',
    labelKey: 'nav.lab',
    icon: 'flask',
    items: [LAB_WORKLIST, LAB_REPORTS, LAB_CONFIG],
  },
  {
    id: 'stocks',
    labelKey: 'nav.stocks',
    icon: 'box',
    items: [STOCK, ADD_STOCK, ISSUE, ADJUST, ACTIVITY, ISSUING_ANALYTICS],
  },
  // No permission on the group itself: `SidebarGroup` already renders nothing
  // when every child is filtered out, so deriving container visibility from the
  // children removes a second thing to keep in step with them.
  { id: 'admin', labelKey: 'nav.admin', icon: 'users', items: [PEOPLE, AUDIT_LOG, ROLES] },
];

export type MobileViewId = 'staff' | 'doctor' | 'lab' | 'stocks' | 'admin';

export interface MobileView {
  id: MobileViewId;
  labelKey: string;
  icon: IconName;
  items: NavLeaf[];
  /**
   * Permission that defines the view, where its identity comes from one child
   * rather than all of them.
   *
   * Groups get by without this because `SidebarGroup` hides itself once every
   * child is filtered out. A mobile view cannot: the doctor view is APT_COUNTER
   * + PATIENTS + STOCK, and two of those are open to everyone — so "has a
   * visible child" would show a clinical view to someone with no clinical
   * access, minus the only entry that made it clinical.
   */
  requires?: PermissionKey;
}

export const MOBILE_VIEWS: MobileView[] = [
  // Staff view (all roles): Registry replaces Patients — non-doctors must not
  // reach the history-bearing full patient record.
  {
    id: 'staff',
    labelKey: 'nav.staffView',
    icon: 'user',
    // Record visit takes the tab Issue used to hold: dispensing to a patient now
    // happens inside the visit, and the bar has room for four. Standalone Issue
    // is still one tap away in the Stocks view.
    items: [REGISTRY, VISITS, STOCK, APT_COUNTER],
  },
  // Doctor view: the clinical surface — the patient being seen now, the full
  // patient record, medicine stock at a glance, and the appointment counter. Visible to whoever can open the
  // clinical record, which PATIENTS declares.
  {
    id: 'doctor',
    labelKey: 'nav.doctorView',
    icon: 'grid',
    requires: 'clinical.consultations.read',
    items: [CONSULTING, PATIENTS, STOCK, APT_COUNTER],
  },
  // The lab, as its own view rather than two leaves borrowed by other views.
  //
  // The worklist used to sit in the staff view between Issue and Stock, and
  // Reports in the doctor view — so on mobile the lab read as a corner of the
  // dispensary, and the test list (the only place a clinic edits what it
  // offers) was unreachable altogether. Mirrors the desktop `lab` group, which
  // has held all three since the lab shipped.
  //
  // No `requires`: every child names a lab key, so the view disappears on its
  // own for a member holding none of them — same reasoning as the admin view.
  {
    id: 'lab',
    labelKey: 'nav.lab',
    icon: 'flask',
    items: [LAB_WORKLIST, LAB_REPORTS, LAB_CONFIG],
  },
  {
    id: 'stocks',
    labelKey: 'nav.stocks',
    icon: 'box',
    items: [ADD_STOCK, STOCK, ISSUE, ADJUST, ACTIVITY],
  },
  {
    id: 'admin',
    labelKey: 'nav.admin',
    icon: 'users',
    items: [PEOPLE, AUDIT_LOG, ROLES, ISSUING_ANALYTICS],
  },
];

export const ALL_LEAVES: NavLeaf[] = [HOME, CONSULTING, PATIENTS, ...GROUPS.flatMap((g) => g.items)];

/**
 * The mobile views a member can see, each carrying only the leaves they can
 * open.
 *
 * Both parts matter, and the second one used to be missing. A view is dropped
 * when its own key is denied or when none of its children are visible; its
 * ITEMS are then filtered as well, because the bottom bar and the drawer's
 * "jump to the first item" both read this list. Without the filter a dispenser
 * without `inventory.stock.issue` was shown an Issue tab — the same door-onto-a
 * -403 that every `requires` above exists to prevent, and which `SidebarGroup`
 * has always avoided on desktop.
 */
export function visibleMobileViews(can: (key: PermissionKey) => boolean): MobileView[] {
  const allowed = (leaf: NavLeaf) => !leaf.requires || can(leaf.requires);
  return MOBILE_VIEWS.filter((v) => (!v.requires || can(v.requires)) && v.items.some(allowed)).map(
    (v) => ({ ...v, items: v.items.filter(allowed) }),
  );
}

/**
 * The nav leaf a path belongs to, preferring the LONGEST matching prefix.
 *
 * Longest-match, not first-match. Until the lab arrived every nested path
 * belonged to the same leaf as its parent (`/registry/pending` is still
 * Registry), so first-match was indistinguishable from correct. `/lab` and
 * `/lab/reports` are two different destinations, and first-match would light up
 * Worklist while the user is looking at Reports.
 */
export function findLeafByPath(pathname: string): NavLeaf | undefined {
  let best: NavLeaf | undefined;
  for (const leaf of ALL_LEAVES) {
    const matches = leaf.to === '/' ? pathname === '/' : pathname.startsWith(leaf.to);
    if (matches && (!best || leaf.to.length > best.to.length)) best = leaf;
  }
  return best;
}

/**
 * Pick the mobile view a given route belongs to. Home (and unknown routes)
 * yield `null` — the bottom bar hides in that case.
 *
 * When the same leaf appears in multiple views (e.g. Issue is in both Staff
 * and Stocks), prefers a `currentView` hint so navigating between leaves
 * inside the user's selected view doesn't bounce them elsewhere.
 */
export function deriveMobileView(
  pathname: string,
  currentView: MobileViewId | null,
): MobileViewId | null {
  if (pathname === '/') return null;
  const leaf = findLeafByPath(pathname);
  if (!leaf || leaf.to === '/') return null;
  if (currentView) {
    const view = MOBILE_VIEWS.find((v) => v.id === currentView);
    if (view && view.items.some((it) => it.to === leaf.to)) return currentView;
  }
  const match = MOBILE_VIEWS.find((v) => v.items.some((it) => it.to === leaf.to));
  return match?.id ?? null;
}

/**
 * Reconcile the persisted mobile view against the current path AND the views
 * actually visible to this role. A persisted hint is only trusted when it's
 * still visible — otherwise a stale admin-only view (e.g. an admin who used the
 * Doctor view and was later demoted to staff) would resolve to a hidden view
 * and blank the bottom bar. Dropping the hint re-derives a role-appropriate
 * view from the path.
 */
export function reconcileMobileView(
  pathname: string,
  persistedView: MobileViewId | null,
  visibleViewIds: MobileViewId[],
): MobileViewId | null {
  const hint = persistedView && visibleViewIds.includes(persistedView) ? persistedView : null;
  return deriveMobileView(pathname, hint);
}
